import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileSpreadsheet, Trash2, Loader2, Search,
  AlertTriangle, BarChart3, MessageSquare, ChevronDown, FileText,
  Database, Table2, Users, Building2, CreditCard, CheckCircle2, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../config';

const DATA_TYPE_LABELS = {
  plan_kont: { label: 'Plan kont / Salda', icon: Table2, color: 'text-blue-600 bg-blue-50' },
  kontrahenci: { label: 'Kontrahenci', icon: Users, color: 'text-emerald-600 bg-emerald-50' },
  wyciag_bankowy: { label: 'Wyciag bankowy', icon: CreditCard, color: 'text-violet-600 bg-violet-50' },
  faktury: { label: 'Faktury', icon: FileText, color: 'text-amber-600 bg-amber-50' },
  inne: { label: 'Inne dane', icon: Database, color: 'text-slate-600 bg-slate-50' },
};

export default function ImportDataPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [datasetData, setDatasetData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [activeTab, setActiveTab] = useState('data');
  const [question, setQuestion] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadDatasets(); }, []);

  const loadDatasets = async () => {
    try {
      const resp = await axios.get(`${API_URL}/import/datasets`, { headers });
      setDatasets(resp.data);
    } catch { toast.error('Blad ladowania danych'); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.toLowerCase().split('.').pop();
    if (!['csv', 'xml', 'xls', 'xlsx'].includes(ext)) {
      toast.error('Nieobslugiwany format. Dozwolone: CSV, XML, XLS, XLSX');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('data_type', 'auto');

      const resp = await axios.post(`${API_URL}/import/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      toast.success(`Zaimportowano ${resp.data.row_count} wierszy (${resp.data.data_type})`);
      loadDatasets();
      setSelectedDataset(resp.data.id);
      loadDatasetData(resp.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad importu pliku');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const loadDatasetData = async (id) => {
    setLoadingData(true);
    setActiveTab('data');
    setAnalysisResult(null);
    try {
      const resp = await axios.get(`${API_URL}/import/data/${id}`, { headers });
      setDatasetData(resp.data);
      setSelectedDataset(id);
    } catch { toast.error('Blad ladowania danych'); }
    finally { setLoadingData(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/import/data/${id}`, { headers });
      toast.success('Zbior danych usuniety');
      setDatasets(prev => prev.filter(d => d.id !== id));
      if (selectedDataset === id) {
        setSelectedDataset(null);
        setDatasetData(null);
      }
    } catch { toast.error('Blad usuwania'); }
  };

  const handleAnalyze = async (type) => {
    if (!selectedDataset) return;
    if (type === 'qa' && !question.trim()) {
      toast.error('Zadaj pytanie');
      return;
    }

    setAnalyzing(true);
    setAnalysisResult(null);
    try {
      const resp = await axios.post(`${API_URL}/import/analyze`, {
        dataset_id: selectedDataset,
        question: type === 'qa' ? question : '',
        analysis_type: type
      }, { headers, timeout: 120000 });

      setAnalysisResult(resp.data);
      setActiveTab('analysis');
      if (type === 'qa') setQuestion('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad analizy');
    } finally { setAnalyzing(false); }
  };

  const getTypeInfo = (type) => DATA_TYPE_LABELS[type] || DATA_TYPE_LABELS.inne;

  return (
    <div className="min-h-screen bg-[#f8fafb] dark:bg-slate-900">
      {/* Header */}
      <div className="border-b bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="gap-2 text-slate-500">
              <ArrowLeft className="w-4 h-4" /> Powrot
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <Database className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 dark:text-white">Import danych</h1>
                <p className="text-xs text-slate-400">Importuj dane z programow ksiegowych</p>
              </div>
            </div>
          </div>
          <div>
            <input ref={fileInputRef} type="file" accept=".csv,.xml,.xls,.xlsx" onChange={handleUpload} className="hidden" data-testid="import-file-input" />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="import-upload-btn"
              className="gap-2 bg-teal-600 hover:bg-teal-700 text-white"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Importowanie...' : 'Importuj plik'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Datasets list */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
              <h3 className="font-semibold text-sm text-slate-800 dark:text-white mb-3">Twoje zbiory danych</h3>

              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : datasets.length === 0 ? (
                <div className="text-center py-8">
                  <FileSpreadsheet className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">Brak danych. Zaimportuj plik CSV, XML lub XLSX.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {datasets.map((ds) => {
                    const info = getTypeInfo(ds.data_type);
                    const TypeIcon = info.icon;
                    return (
                      <div
                        key={ds.id}
                        onClick={() => loadDatasetData(ds.id)}
                        data-testid={`dataset-${ds.id}`}
                        className={`p-3 rounded-xl cursor-pointer transition-all ${
                          selectedDataset === ds.id
                            ? 'bg-teal-50 dark:bg-teal-900/20 border-2 border-teal-300 dark:border-teal-700'
                            : 'bg-slate-50 dark:bg-slate-700/50 border-2 border-transparent hover:border-slate-200'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${info.color}`}>
                            <TypeIcon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-800 dark:text-white truncate">{ds.filename}</p>
                            <p className="text-xs text-slate-400">{ds.row_count} wierszy | {ds.format}</p>
                          </div>
                          <Button
                            variant="ghost" size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDelete(ds.id); }}
                            className="w-6 h-6 p-0 text-slate-400 hover:text-red-500"
                            data-testid={`delete-dataset-${ds.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Supported formats */}
            <div className="mt-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
              <h4 className="font-medium text-xs text-slate-600 dark:text-slate-300 mb-2">Obslugiwane formaty</h4>
              <div className="space-y-1.5 text-xs text-slate-500">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> CSV (Excel, Symfonia, Optima)</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> XML (JPK_VAT, JPK_FA, JPK_WB)</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> XLS / XLSX (Excel)</div>
              </div>
              <h4 className="font-medium text-xs text-slate-600 dark:text-slate-300 mb-2 mt-3">Typy danych</h4>
              <div className="space-y-1.5 text-xs text-slate-500">
                <div>Plan kont, salda, obroty</div>
                <div>Kontrahenci (NIP, adresy)</div>
                <div>Wyciagi bankowe</div>
                <div>Faktury, rozrachunki</div>
              </div>
            </div>
          </div>

          {/* Main content area */}
          <div className="lg:col-span-3">
            {!selectedDataset ? (
              /* Empty state */
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-12 text-center shadow-sm">
                <Database className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">Importuj dane ksiegowe</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
                  Wgraj plik z programu ksiegowego (CSV, XML, XLSX), a KAI przeanalizuje dane, wykryje anomalie i odpowie na Twoje pytania.
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="gap-2 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="import-upload-empty-btn"
                >
                  <Upload className="w-4 h-4" /> Wybierz plik
                </Button>
              </div>
            ) : loadingData ? (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border p-12 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              </div>
            ) : datasetData ? (
              <div className="space-y-4">
                {/* Dataset info header */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeInfo(datasetData.data_type).color}`}>
                        {React.createElement(getTypeInfo(datasetData.data_type).icon, { className: "w-5 h-5" })}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 dark:text-white">{datasetData.filename}</h3>
                        <p className="text-xs text-slate-400">
                          {datasetData.row_count} wierszy | {datasetData.columns?.length} kolumn | {getTypeInfo(datasetData.data_type).label}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleAnalyze('anomalies')}
                        disabled={analyzing}
                        data-testid="analyze-anomalies-btn"
                        className="gap-1.5 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                      >
                        {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                        Wykryj anomalie
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleAnalyze('report')}
                        disabled={analyzing}
                        data-testid="analyze-report-btn"
                        className="gap-1.5 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5" />}
                        Raport
                      </Button>
                    </div>
                  </div>

                  {/* Q&A input */}
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAnalyze('qa')}
                      placeholder="Zadaj pytanie o dane, np. 'Jaki jest saldo konta 200?' lub 'Pokaż kontrahentów z największym obrotem'"
                      className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      data-testid="import-question-input"
                    />
                    <Button
                      onClick={() => handleAnalyze('qa')}
                      disabled={analyzing || !question.trim()}
                      data-testid="import-question-btn"
                      className="bg-teal-600 hover:bg-teal-700 text-white px-4"
                    >
                      {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2">
                  <Button
                    variant={activeTab === 'data' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('data')}
                    data-testid="tab-data"
                    className={`gap-1.5 ${activeTab === 'data' ? 'bg-teal-600 text-white' : 'text-slate-600'}`}
                  >
                    <Table2 className="w-4 h-4" /> Dane
                  </Button>
                  {analysisResult && (
                    <Button
                      variant={activeTab === 'analysis' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setActiveTab('analysis')}
                      data-testid="tab-analysis"
                      className={`gap-1.5 ${activeTab === 'analysis' ? 'bg-teal-600 text-white' : 'text-slate-600'}`}
                    >
                      <MessageSquare className="w-4 h-4" /> Analiza
                    </Button>
                  )}
                </div>

                {/* Data table */}
                {activeTab === 'data' && (
                  <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600">
                            <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 w-12">#</th>
                            {datasetData.columns?.map((col, i) => (
                              <th key={i} className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {datasetData.data?.slice(0, 100).map((row, rIdx) => (
                            <tr key={rIdx} className={`border-b border-slate-100 dark:border-slate-700 ${rIdx % 2 === 0 ? '' : 'bg-slate-50/50 dark:bg-slate-700/20'}`}>
                              <td className="px-3 py-2 text-xs text-slate-400">{rIdx + 1}</td>
                              {datasetData.columns?.map((col, cIdx) => (
                                <td key={cIdx} className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap max-w-[200px] truncate">
                                  {row[col] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {datasetData.data?.length > 100 && (
                      <div className="p-3 text-center text-xs text-slate-400 border-t">
                        Wyswietlono 100 z {datasetData.row_count} wierszy
                      </div>
                    )}
                  </div>
                )}

                {/* Analysis result */}
                {activeTab === 'analysis' && analysisResult && (
                  <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      {analysisResult.analysis_type === 'anomalies' && <AlertTriangle className="w-5 h-5 text-amber-500" />}
                      {analysisResult.analysis_type === 'report' && <BarChart3 className="w-5 h-5 text-blue-500" />}
                      {analysisResult.analysis_type === 'qa' && <MessageSquare className="w-5 h-5 text-teal-500" />}
                      <h3 className="font-semibold text-slate-800 dark:text-white">
                        {analysisResult.analysis_type === 'anomalies' ? 'Wykryte anomalie' :
                         analysisResult.analysis_type === 'report' ? 'Raport' : 'Odpowiedz'}
                      </h3>
                      <span className="text-xs text-slate-400 ml-auto">
                        Przeanalizowano {analysisResult.rows_analyzed} wierszy
                      </span>
                    </div>
                    <div className="prose prose-sm max-w-none text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-sm leading-relaxed" data-testid="analysis-result">
                      {analysisResult.analysis}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
